import { defineStore } from 'pinia';

import type { ComparisonControls } from '@/features/Comparison/Components/types';
import type {
  FeatureGroup,
  FeatureItem,
  TariffFeatures,
  ValuesByIdentifier,
} from '@/features/Features/types';
import {
  buildFeatureData,
  transformFeaturesToIdentifierObject,
} from '@/features/Features/utils';
import { useTariffStore } from '@/features/Tariffs/store';
import { useApi } from '@/services/api/useApi';
import { VGL_ERROR_CODE } from '@/services/errorHandler/errorCodes';
import { getErrorMessage } from '@/services/errorHandler/utils';
import { VglError } from '@/services/errorHandler/vglError';
import type { ResourceContainer } from '@/types';

export interface FeaturesStoreState extends ResourceContainer<FeatureGroup[]> {
  collapsed: string[];
  tariffFeatures: {
    [k: string]: TariffFeatures;
  };
  comparisonControls: ComparisonControls;
}

export enum FEATURE {
  UNCHECKED,
  CHECKED,
}

export const useFeaturesStore = defineStore('FeatureStore', {
  state: (): FeaturesStoreState => ({
    data: [],
    collapsed: [],
    isLoading: false,
    tariffFeatures: {},
    comparisonControls: {
      expanded: true,
      onlyWished: false,
      hideMatches: false,
      hints: true,
    },
  }),

  actions: {
    async loadFeatures() {
      try {
        this.isLoading = true;
        const featuresApi = useApi().features;
        const [, request] = featuresApi.loadFeatures();

        const result = await request;
        if (result === null) {
          throw new VglError(VGL_ERROR_CODE.GENERAL_UNEXPECTED_EMPTY_RESPONSE, {
            additionalErrorMessage: 'features -> loadFeatures',
          });
        }

        this.data = buildFeatureData(result);
      } catch (error) {
        throw new VglError(VGL_ERROR_CODE.FEATURES_LOADING_FAILED, {
          additionalErrorMessage: `Error: ${getErrorMessage(error)}`,
          cause: error,
        });
      } finally {
        this.isLoading = false;
      }
    },

    setFeatureByIdentifier(identifier: string, value: number) {
      const feature = this.byIdentifier[identifier];
      if (feature === undefined) {
        throw new VglError(VGL_ERROR_CODE.FEATURES_MISSING, {
          additionalErrorMessage: `Identifier: ${identifier} value: ${value} features loaded: ${JSON.stringify(
            this.byIdentifier,
          )}`,
        });
      }

      feature.value = value;
    },

    async loadTariffFeatures() {
      const featuresApi = useApi().features;
      const tariffStore = useTariffStore();
      const { calculationId } = tariffStore;

      const results: Promise<void>[] = [];

      tariffStore.selectedTariffsWithAllTimeTariff.forEach((tariff) => {
        if (this.tariffFeatures[tariff.identifier] === undefined) {
          const isAllTimeTariff = tariffStore.allTimeTariffs.some(
            (t) => t.identifier === tariff.identifier,
          );
          const [, resultPromise] = featuresApi.loadTariffFeatures(
            tariff.identifier,
            calculationId,
            isAllTimeTariff,
          );

          results.push(
            resultPromise.then((result) => {
              if (result === null) {
                throw new VglError(
                  VGL_ERROR_CODE.GENERAL_UNEXPECTED_EMPTY_RESPONSE,
                  {
                    additionalErrorMessage: 'features -> loadTariffFeatures',
                  },
                );
              }

              const { merkmale } = result;
              const mappedByMerkmalKey: TariffFeatures = {};

              merkmale.forEach((merkmal) => {
                mappedByMerkmalKey[merkmal.key] = merkmal;
              });
              this.tariffFeatures[tariff.identifier] = mappedByMerkmalKey;
            }),
          );
        }
      });

      await Promise.all(results).catch((error) => {
        throw new VglError(VGL_ERROR_CODE.FEATURES_TARIFF_LOADING_FAILED, {
          additionalErrorMessage: `Error: ${getErrorMessage(
            error,
          )} - CalculationId: ${calculationId}`,
          cause: error,
        });
      });
    },

    clearCachedTariffFeatures() {
      this.tariffFeatures = {};
    },

    handleCollapsedUpdate(show: boolean, label: string) {
      if (show) {
        this.collapsed.splice(this.collapsed.indexOf(label), 1);
      } else {
        this.collapsed.push(label);
      }
    },

    resetCollapsedFeatures() {
      this.collapsed = [];
    },

    uncheckFeatureGroup(rubrikIdentifier: string) {
      const data = this.byIdentifier;

      Object.keys(data).forEach((identifier) => {
        if (identifier.startsWith(rubrikIdentifier)) {
          data[identifier].value = FEATURE.UNCHECKED;
        }
      });
    },
  },

  getters: {
    byIdentifier: (state) =>
      transformFeaturesToIdentifierObject(state.data as FeatureGroup[]),

    valuesByIdentifier() {
      return Object.entries(this.byIdentifier).reduce(
        (acc, [key, value]: [string, FeatureItem]) => {
          if (value.type === 'select' && value.choices) {
            acc[key] = {
              [FEATURE.UNCHECKED]: 'Keine Vorgabe',
              ...value.choices.reduce(
                (nestedAcc, choice) => ({
                  ...nestedAcc,
                  [choice.value]: choice.text,
                }),
                {} as { [k: number]: string },
              ),
            };
          } else {
            acc[key] = {
              [FEATURE.UNCHECKED]: 'Keine Vorgabe',
              [FEATURE.CHECKED]: 'Gewünscht',
            };
          }

          return acc;
        },
        {} as ValuesByIdentifier,
      );
    },
  },
});
